import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Calculator, TrendingUp, TrendingDown, Receipt, CalendarClock,
  MessageSquare, BookOpen, Settings, Plus, X, CheckCircle, Clock,
  AlertTriangle, Wallet, Landmark, Info, Trash2, Edit2, Eye,
} from 'lucide-react';
import { fmtFecha } from '../../utils/fecha';
import ManualContable from './ManualContable';
import * as C from '../../db/contabilidad';

import { fmtMoneda as fmt$ } from '../../utils/moneda';
import { getAbrev } from '../../utils/marca';

// Rango por defecto: año en curso.
const hoy = () => new Date();
const isoDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const TABS = [
  { key: 'resumen',      label: 'Resumen',        icon: Calculator },
  { key: 'ingresos',     label: 'Ingresos',       icon: TrendingUp },
  { key: 'gastos',       label: 'Gastos',         icon: TrendingDown },
  { key: 'obligaciones', label: 'Obligaciones',   icon: CalendarClock },
  { key: 'notas',        label: 'Recomendaciones', icon: MessageSquare },
  { key: 'manual',       label: 'Manual',         icon: BookOpen },
  { key: 'config',       label: 'Configuración',  icon: Settings },
];

export default function Contabilidad() {
  const { canEdit } = useApp();
  const editable = canEdit('contabilidad');
  const [tab, setTab] = useState('resumen');
  const [config, setConfig] = useState(null);

  // Rango de fechas (compartido por Resumen / Ingresos / Gastos)
  const y = hoy().getFullYear();
  const [desde, setDesde] = useState(`${y}-01-01`);
  const [hasta, setHasta] = useState(`${y}-12-31`);

  const loadConfig = useCallback(async () => {
    setConfig(await C.getConfig());
  }, []);
  useEffect(() => { loadConfig(); }, [loadConfig]);

  if (!config) return null;

  const tipoLabel = config.tipo_contribuyente === 'juridica' ? 'Persona Jurídica' : 'Persona Natural';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Contabilidad</div>
          <div className="page-subtitle">
            Régimen actual: <strong style={{ color: 'var(--sky-light)' }}>{tipoLabel}</strong>
            {' · '}Tu confianza, es nuestra cobertura.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="toolbar" style={{ gap: 6 }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={active ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Selector de rango (solo en pestañas financieras) */}
      {['resumen', 'ingresos', 'gastos'].includes(tab) && (
        <div className="toolbar" style={{ marginBottom: 18 }}>
          <div className="form-group">
            <label className="form-label">Desde</label>
            <input type="date" className="form-input" value={desde} onChange={e => setDesde(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Hasta</label>
            <input type="date" className="form-input" value={hasta} onChange={e => setHasta(e.target.value)} />
          </div>
          <RangoRapido setDesde={setDesde} setHasta={setHasta} />
        </div>
      )}

      {tab === 'resumen' && <Resumen desde={desde} hasta={hasta} config={config} />}
      {tab === 'ingresos' && <Ingresos desde={desde} hasta={hasta} editable={editable} />}
      {tab === 'gastos' && <Gastos desde={desde} hasta={hasta} editable={editable} />}
      {tab === 'obligaciones' && <Obligaciones config={config} editable={editable} />}
      {tab === 'notas' && <Notas editable={editable} />}
      {tab === 'manual' && <ManualContable />}
      {tab === 'config' && <ConfigContable config={config} onSaved={loadConfig} editable={editable} />}
    </div>
  );
}

// ───────────────────────────────────────────── Rangos rápidos
function RangoRapido({ setDesde, setHasta }) {
  const y = hoy().getFullYear();
  const m = hoy().getMonth();
  const set = (d, h) => { setDesde(d); setHasta(h); };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => set(`${y}-01-01`, `${y}-12-31`)}>Año {y}</button>
      <button className="btn btn-secondary btn-sm" onClick={() => {
        const d = new Date(y, m, 1), h = new Date(y, m + 1, 0);
        set(isoDay(d), isoDay(h));
      }}>Mes actual</button>
      <button className="btn btn-secondary btn-sm" onClick={() => set(`${y - 1}-01-01`, `${y - 1}-12-31`)}>Año {y - 1}</button>
    </div>
  );
}

// ───────────────────────────────────────────── RESUMEN
function Resumen({ desde, hasta, config }) {
  const abrev = getAbrev();
  const [r, setR] = useState(null);
  useEffect(() => { C.getResumen(desde, hasta).then(setR); }, [desde, hasta]);
  if (!r) return <div className="empty-state">Calculando…</div>;

  const isr = C.desgloseISR(r.rentaNetaEstimada, config.tipo_contribuyente);

  const Stat = ({ label, value, icon: Icon, color = 'var(--sky)', hint }) => (
    <div className="stat-card">
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 6 }}>{hint}</div>}
      {Icon && <Icon size={40} className="stat-icon" />}
    </div>
  );

  return (
    <div>
      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="Ingresos del período" value={fmt$(r.ingresoTotal)} icon={TrendingUp} color="#22C55E"
          hint={`Comisión ${abrev} ${fmt$(r.comisionNeta)} + otros ingresos ${fmt$(r.otrosIngresos)}`} />
        <Stat label="Gastos del negocio" value={fmt$(r.gastosTotal)} icon={TrendingDown} color="#F59E0B"
          hint={`${r.numGastos} gasto(s) registrado(s)`} />
        <Stat label="Utilidad de caja" value={fmt$(r.utilidad)} icon={Wallet}
          color={r.utilidad >= 0 ? '#22C55E' : '#EF4444'}
          hint="Ingreso neto menos gastos" />
      </div>

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Landmark size={18} style={{ color: 'var(--sky)' }} />
            <div style={{ fontWeight: 800, fontSize: 16 }}>Estimación de Impuesto sobre la Renta</div>
          </div>
          <Linea label="Renta neta gravable estimada" value={fmt$(r.rentaNetaEstimada)} />
          <Linea label="Tramo aplicado" value={isr.tramo} small />
          <Linea label="Tasa efectiva" value={`${isr.efectiva.toFixed(1)}%`} small />
          <div className="divider" />
          <Linea label="ISR estimado del período" value={fmt$(isr.isr)} bold color="#F59E0B" />
          <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 10, lineHeight: 1.5 }}>
            <Info size={12} style={{ verticalAlign: 'middle' }} /> Estimación informativa basada en
            ingresos y gastos deducibles registrados. El cálculo oficial lo confirma tu contador con la
            declaración anual.
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <Receipt size={18} style={{ color: 'var(--sky)' }} />
            <div style={{ fontWeight: 800, fontSize: 16 }}>Cuentas pendientes (operación)</div>
          </div>
          <Linea label={`Comisión ${abrev} esperando aseguradora`} value={fmt$(r.ruEsperandoAseg)} color="#8b5cf6" />
          <Linea label={`Comisión ${abrev} lista para liquidar`} value={fmt$(r.ruListoLiquidar)} color="var(--sky-light)" />
          <Linea label="Por pagar a agentes (pasivo)" value={fmt$(r.porPagarAgentes)} color="#F59E0B" />
          <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 10, lineHeight: 1.5 }}>
            <Info size={12} style={{ verticalAlign: 'middle' }} /> Vienen automáticamente del módulo de
            Comisiones (mismos números). El ingreso de arriba es la comisión {abrev} ya cerrada; esto es lo que
            falta por realizar. La prima de los clientes NO se cuenta como ingreso.
          </div>
        </div>
      </div>
    </div>
  );
}

const Linea = ({ label, value, bold, small, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', gap: 12 }}>
    <span style={{ color: 'var(--silver)', fontSize: small ? 13 : 14 }}>{label}</span>
    <span style={{ fontWeight: bold ? 800 : 600, fontSize: small ? 13 : 15, color: color || '#fff', textAlign: 'right' }}>{value}</span>
  </div>
);

// ───────────────────────────────────────────── INGRESOS
const INGRESO_VACIO = () => ({
  fecha: isoDay(hoy()), categoria_id: '', descripcion: '', fuente: '',
  monto: '', itbms: '', notas: '',
});

function Ingresos({ desde, hasta, editable }) {
  const abrev = getAbrev();
  const [comis, setComis] = useState([]);
  const [manuales, setManuales] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [c, m, k] = await Promise.all([
      C.getIngresosPeriodo(desde, hasta),
      C.listIngresosManuales(desde, hasta),
      C.listCategorias('ingreso'),
    ]);
    setComis(c); setManuales(m); setCats(k); setLoading(false);
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const totalRU = comis.reduce((s, c) => s + Number(c.monto_ru || 0), 0);
  const totalManual = manuales.reduce((s, i) => s + Number(i.monto || 0), 0);
  const totalGeneral = totalRU + totalManual;

  const openNew = () => setModal({ form: INGRESO_VACIO(), id: null });
  const openEdit = i => setModal({ id: i.id, form: {
    fecha: i.fecha, categoria_id: i.categoria_id || '', descripcion: i.descripcion || '',
    fuente: i.fuente || '', monto: i.monto ?? '', itbms: i.itbms ?? '', notas: i.notas || '',
  } });
  const upd = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));

  const save = async () => {
    if (!modal.form.descripcion.trim()) { alert('Escribe una descripción.'); return; }
    if (!(Number(modal.form.monto) > 0)) { alert('El monto debe ser mayor a 0.'); return; }
    setSaving(true);
    try {
      await C.saveIngreso({ ...modal.form, categoria_id: modal.form.categoria_id || null }, modal.id);
      setModal(null); await load();
    } catch (e) { alert('Error al guardar: ' + (e.message || e)); }
    finally { setSaving(false); }
  };

  const remove = async i => {
    if (!window.confirm(`¿Eliminar el ingreso "${i.descripcion}"?`)) return;
    try { await C.deleteIngreso(i.id); await load(); }
    catch (e) { alert('Error al eliminar: ' + (e.message || e)); }
  };

  if (loading) return <div className="empty-state">Cargando ingresos…</div>;

  return (
    <div>
      {/* Totales */}
      <div className="grid-3" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--text-success)' }}>{fmt$(totalGeneral)}</div>
          <div className="stat-label">Ingresos totales del período</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--sky-light)' }}>{fmt$(totalRU)}</div>
          <div className="stat-label">Comisión {abrev} (automático)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--text-purple)' }}>{fmt$(totalManual)}</div>
          <div className="stat-label">Otros ingresos (registrados)</div>
        </div>
      </div>

      {/* Otros ingresos manuales */}
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Otros ingresos</div>
        {editable && <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={15} /> Registrar ingreso</button>}
      </div>
      <div className="card" style={{ padding: 0, marginBottom: 24 }}>
        {manuales.length === 0 ? (
          <div className="empty-state">
            <TrendingUp size={40} className="empty-state-icon" />
            <div className="empty-state-title">Sin otros ingresos en este período</div>
            <div>Registra aquí sobrecomisiones, bonos/concursos, fees por referir, honorarios, etc.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr>
                <th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Fuente</th>
                <th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'right' }}>ITBMS</th>
                <th style={{ textAlign: 'right' }}>Total</th>{editable && <th></th>}
              </tr></thead>
              <tbody>
                {manuales.map(i => (
                  <tr key={i.id}>
                    <td>{fmtFecha(i.fecha)}</td>
                    <td>{i.cont_categorias?.nombre || '—'}</td>
                    <td>{i.descripcion}</td>
                    <td>{i.fuente || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(i.monto)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(i.itbms)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-success)' }}>{fmt$(i.total)}</td>
                    {editable && (
                      <td><div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-icon" onClick={() => openEdit(i)} title="Editar"><Edit2 size={14} /></button>
                        <button className="btn btn-danger btn-icon" onClick={() => remove(i)} title="Eliminar"><Trash2 size={14} /></button>
                      </div></td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Comisiones automáticas */}
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Comisiones cobradas (automático)</div>
        <div style={{ color: 'var(--silver)', fontSize: 13 }}>
          <Info size={13} style={{ verticalAlign: 'middle' }} /> Igual que "{abrev} Cobrado" en Comisiones.
        </div>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {comis.length === 0 ? (
          <div className="empty-state">
            <TrendingUp size={40} className="empty-state-icon" />
            <div className="empty-state-title">Sin comisiones cobradas en este período</div>
            <div>El ingreso aparece cuando cierras (liquidas) la comisión de {abrev} en el módulo de Comisiones.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr>
                <th>Fecha cobro</th><th>Póliza</th><th>Cliente</th><th>Aseguradora</th>
                <th>Agente</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Comisión neta {abrev}</th>
              </tr></thead>
              <tbody>
                {comis.map(c => (
                  <tr key={c.id}>
                    <td>{fmtFecha(c.ru_liquidado_en?.slice(0, 10))}</td>
                    <td>{c.polizas?.numero || '—'}</td>
                    <td>{c.polizas?.clientes?.nombre || '—'}</td>
                    <td>{c.polizas?.aseguradoras?.nombre || '—'}</td>
                    <td>{c.agentes?.nombre || `${abrev} directo`}</td>
                    <td><span className="badge badge-neutral">{c.tipo || '—'}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-success)' }}>{fmt$(c.monto_ru)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{modal.id ? 'Editar ingreso' : 'Registrar ingreso'}</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input type="date" className="form-input" value={modal.form.fecha} onChange={e => upd('fecha', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-select" value={modal.form.categoria_id} onChange={e => upd('categoria_id', e.target.value)}>
                    <option value="">Sin categoría</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Descripción *</label>
                <input className="form-input" value={modal.form.descripcion} placeholder="Ej: Sobrecomisión ASSA 1er semestre"
                  onChange={e => upd('descripcion', e.target.value)} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Fuente (quién paga)</label>
                <input className="form-input" value={modal.form.fuente} placeholder="Aseguradora, cliente, etc."
                  onChange={e => upd('fuente', e.target.value)} />
              </div>
              <div className="form-grid form-grid-2" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Monto *</label>
                  <input type="number" step="0.01" className="form-input" value={modal.form.monto} onChange={e => upd('monto', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">ITBMS (si aplica)</label>
                  <input type="number" step="0.01" className="form-input" value={modal.form.itbms} onChange={e => upd('itbms', e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Notas</label>
                <textarea className="form-textarea" value={modal.form.notas} onChange={e => upd('notas', e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────── GASTOS
const GASTO_VACIO = () => ({
  fecha: isoDay(hoy()), categoria_id: '', descripcion: '', proveedor: '',
  monto: '', itbms: '', deducible: true, forma_pago: 'transferencia', num_factura: '', notas: '',
});

function Gastos({ desde, hasta, editable }) {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [modal, setModal] = useState(null); // null | {form, id}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setRows(await C.listGastos(desde, hasta));
    setCats(await C.listCategorias('gasto'));
  }, [desde, hasta]);
  useEffect(() => { load(); }, [load]);

  const total = rows.reduce((s, g) => s + Number(g.total || 0), 0);

  const openNew = () => setModal({ form: GASTO_VACIO(), id: null });
  const openEdit = g => setModal({
    id: g.id,
    form: {
      fecha: g.fecha, categoria_id: g.categoria_id || '', descripcion: g.descripcion || '',
      proveedor: g.proveedor || '', monto: g.monto ?? '', itbms: g.itbms ?? '',
      deducible: g.deducible, forma_pago: g.forma_pago || 'transferencia',
      num_factura: g.num_factura || '', notas: g.notas || '',
    },
  });

  const upd = (k, v) => setModal(m => ({ ...m, form: { ...m.form, [k]: v } }));

  const save = async () => {
    if (!modal.form.descripcion.trim()) { alert('Escribe una descripción del gasto.'); return; }
    if (!(Number(modal.form.monto) > 0)) { alert('El monto debe ser mayor a 0.'); return; }
    setSaving(true);
    try {
      await C.saveGasto({
        ...modal.form,
        categoria_id: modal.form.categoria_id || null,
        creado_por: undefined,
      }, modal.id);
      setModal(null);
      await load();
    } catch (e) {
      alert('Error al guardar: ' + (e.message || e));
    } finally { setSaving(false); }
  };

  const remove = async g => {
    if (!window.confirm(`¿Eliminar el gasto "${g.descripcion}"?`)) return;
    try { await C.deleteGasto(g.id); await load(); }
    catch (e) { alert('Error al eliminar: ' + (e.message || e)); }
  };

  return (
    <div>
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-warning)' }}>Total gastos: {fmt$(total)}</div>
        {editable && <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={15} /> Registrar gasto</button>}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {rows.length === 0 ? (
          <div className="empty-state">
            <Receipt size={40} className="empty-state-icon" />
            <div className="empty-state-title">Sin gastos en este período</div>
            <div>Registra aquí alquiler, software, honorarios, publicidad, etc.</div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Proveedor</th>
                  <th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'right' }}>ITBMS</th>
                  <th style={{ textAlign: 'right' }}>Total</th><th>Deducible</th>
                  {editable && <th></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(g => (
                  <tr key={g.id}>
                    <td>{fmtFecha(g.fecha)}</td>
                    <td>{g.cont_categorias?.nombre || '—'}</td>
                    <td>{g.descripcion}</td>
                    <td>{g.proveedor || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(g.monto)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt$(g.itbms)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt$(g.total)}</td>
                    <td>{g.deducible
                      ? <span className="badge badge-success">Sí</span>
                      : <span className="badge badge-neutral">No</span>}</td>
                    {editable && (
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-icon" onClick={() => openEdit(g)} title="Editar"><Edit2 size={14} /></button>
                          <button className="btn btn-danger btn-icon" onClick={() => remove(g)} title="Eliminar"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{modal.id ? 'Editar gasto' : 'Registrar gasto'}</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <input type="date" className="form-input" value={modal.form.fecha} onChange={e => upd('fecha', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select className="form-select" value={modal.form.categoria_id} onChange={e => {
                    const id = e.target.value;
                    upd('categoria_id', id);
                    const cat = cats.find(c => String(c.id) === String(id));
                    if (cat) upd('deducible', cat.deducible);
                  }}>
                    <option value="">Sin categoría</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Descripción *</label>
                <input className="form-input" value={modal.form.descripcion} placeholder="Ej: Alquiler oficina junio"
                  onChange={e => upd('descripcion', e.target.value)} />
              </div>
              <div className="form-grid form-grid-2" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Proveedor</label>
                  <input className="form-input" value={modal.form.proveedor} onChange={e => upd('proveedor', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">N° factura / recibo</label>
                  <input className="form-input" value={modal.form.num_factura} onChange={e => upd('num_factura', e.target.value)} />
                </div>
              </div>
              <div className="form-grid form-grid-3" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Monto (sin ITBMS) *</label>
                  <input type="number" step="0.01" className="form-input" value={modal.form.monto} onChange={e => upd('monto', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">ITBMS pagado</label>
                  <input type="number" step="0.01" className="form-input" value={modal.form.itbms} onChange={e => upd('itbms', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Forma de pago</label>
                  <select className="form-select" value={modal.form.forma_pago} onChange={e => upd('forma_pago', e.target.value)}>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="ach">ACH</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="ded" checked={!!modal.form.deducible} onChange={e => upd('deducible', e.target.checked)} />
                <label htmlFor="ded" style={{ color: '#cdd9e5', fontSize: 14 }}>
                  Es un gasto del negocio (deducible del ISR)
                </label>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Notas</label>
                <textarea className="form-textarea" value={modal.form.notas} onChange={e => upd('notas', e.target.value)} />
              </div>
              <div style={{ fontSize: 13, color: 'var(--silver)', marginTop: 10, padding: 10, background: 'rgba(245, 158, 11, 0.08)', borderRadius: 8 }}>
                <AlertTriangle size={13} style={{ verticalAlign: 'middle', color: 'var(--text-warning)' }} /> No registres aquí las
                comisiones de agentes (salen solas de la operación) ni gastos personales.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────── OBLIGACIONES
function Obligaciones({ config, editable }) {
  const anioActual = hoy().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [pagoModal, setPagoModal] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [detalle, setDetalle] = useState(null); // { ev, calc }

  const load = useCallback(async () => {
    setLoading(true);
    const [evs, c] = await Promise.all([C.listEventos(anio), C.getContextoObligaciones(anio)]);
    setEventos(evs); setCtx(c);
    setLoading(false);
  }, [anio]);
  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    setGenerando(true);
    try {
      await C.generarEventosAnio(anio, config);
      await load();
    } catch (e) { alert('Error al generar el calendario: ' + (e.message || e)); }
    finally { setGenerando(false); }
  };

  const marcarPagado = async () => {
    try {
      await C.updateEvento(pagoModal.id, {
        estado: 'pagado',
        fecha_pago: pagoModal.fecha_pago || isoDay(hoy()),
        monto_pagado: Number(pagoModal.monto_pagado || 0),
        num_comprobante: pagoModal.num_comprobante || null,
      });
      setPagoModal(null);
      await load();
    } catch (e) { alert('Error: ' + (e.message || e)); }
  };

  const hoyISO = isoDay(hoy());
  const estadoEvento = ev => {
    if (ev.estado === 'pagado') return { txt: 'Pagado', cls: 'badge-success', icon: CheckCircle };
    if (ev.fecha_limite < hoyISO) return { txt: 'Vencido', cls: 'badge-danger', icon: AlertTriangle };
    const diff = (new Date(ev.fecha_limite) - new Date(hoyISO)) / 86400000;
    if (diff <= 15) return { txt: 'Por vencer', cls: 'badge-warning', icon: Clock };
    return { txt: 'Pendiente', cls: 'badge-neutral', icon: Clock };
  };

  return (
    <div>
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <div className="form-group" style={{ maxWidth: 140 }}>
          <label className="form-label">Año</label>
          <select className="form-select" value={anio} onChange={e => setAnio(Number(e.target.value))}>
            {[anioActual - 1, anioActual, anioActual + 1].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {editable && (
          <button className="btn btn-primary btn-sm" onClick={generar} disabled={generando}>
            <CalendarClock size={15} /> {generando ? 'Generando…' : 'Generar calendario del año'}
          </button>
        )}
      </div>

      {loading ? <div className="empty-state">Cargando…</div> : eventos.length === 0 ? (
        <div className="card empty-state">
          <CalendarClock size={40} className="empty-state-icon" />
          <div className="empty-state-title">Aún no hay vencimientos para {anio}</div>
          <div>Pulsa “Generar calendario del año” para crear los vencimientos que te aplican
            según tu régimen ({config.tipo_contribuyente === 'juridica' ? 'Persona Jurídica' : 'Persona Natural'}).</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Vence</th><th>Obligación</th><th>Entidad</th><th style={{ textAlign: 'right' }}>Monto est.</th><th>Período</th><th>Estado</th>{editable && <th></th>}</tr>
              </thead>
              <tbody>
                {eventos.map(ev => {
                  const e = estadoEvento(ev);
                  const Icon = e.icon;
                  const calc = ctx ? C.calcularObligacion(ev, config, ctx) : null;
                  return (
                    <tr key={ev.id}>
                      <td>{fmtFecha(ev.fecha_limite)}</td>
                      <td>{ev.cont_obligaciones?.nombre || '—'}</td>
                      <td><span className="badge badge-info">{ev.cont_obligaciones?.entidad}</span></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {calc && calc.monto != null
                          ? <strong>{fmt$(calc.monto)}</strong>
                          : <span style={{ color: 'var(--silver)' }}>—</span>}
                        <button className="btn btn-secondary btn-icon" style={{ marginLeft: 6 }} title="Ver de dónde sale" onClick={() => setDetalle({ ev, calc })}><Eye size={13} /></button>
                      </td>
                      <td style={{ fontSize: 13 }}>{ev.periodo}</td>
                      <td><span className={`badge ${e.cls}`}><Icon size={11} style={{ marginRight: 4 }} />{e.txt}</span></td>
                      {editable && (
                        <td>
                          {ev.estado !== 'pagado'
                            ? <button className="btn btn-success btn-sm" onClick={() => setPagoModal({ id: ev.id, nombre: ev.cont_obligaciones?.nombre, fecha_pago: isoDay(hoy()), monto_pagado: (calc && calc.monto != null) ? calc.monto.toFixed(2) : '', num_comprobante: '' })}>Marcar pagado</button>
                            : <span style={{ fontSize: 12, color: 'var(--silver)' }}>{fmtFecha(ev.fecha_pago)} · {fmt$(ev.monto_pagado)}</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pagoModal && (
        <div className="modal-overlay" onClick={() => setPagoModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div className="modal-title">Marcar como pagado</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setPagoModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#cdd9e5', marginBottom: 14 }}>{pagoModal.nombre}</p>
              <div className="form-group">
                <label className="form-label">Fecha de pago</label>
                <input type="date" className="form-input" value={pagoModal.fecha_pago} onChange={e => setPagoModal(p => ({ ...p, fecha_pago: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Monto pagado</label>
                <input type="number" step="0.01" className="form-input" value={pagoModal.monto_pagado} onChange={e => setPagoModal(p => ({ ...p, monto_pagado: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">N° comprobante</label>
                <input className="form-input" value={pagoModal.num_comprobante} onChange={e => setPagoModal(p => ({ ...p, num_comprobante: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPagoModal(null)}>Cancelar</button>
              <button className="btn btn-success" onClick={marcarPagado}>Confirmar pago</button>
            </div>
          </div>
        </div>
      )}

      {detalle && (
        <div className="modal-overlay" onClick={() => setDetalle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">Detalle del cálculo</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setDetalle(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{detalle.ev.cont_obligaciones?.nombre}</div>
              <div style={{ fontSize: 13, color: 'var(--silver)', marginBottom: 14 }}>
                {detalle.ev.cont_obligaciones?.entidad} · vence {fmtFecha(detalle.ev.fecha_limite)}
                {detalle.ev.cont_obligaciones?.tasa_referencia ? ` · ${detalle.ev.cont_obligaciones.tasa_referencia}` : ''}
              </div>

              {detalle.calc && detalle.calc.detalle && detalle.calc.detalle.length > 0 ? (
                <div style={{ background: 'rgba(var(--navy-rgb),0.5)', borderRadius: 8, padding: '6px 14px' }}>
                  {detalle.calc.detalle.map((d, i) => (
                    <Linea key={i} label={d.label} value={d.valor != null ? fmt$(d.valor) : (d.texto || '')} small />
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--silver)', fontSize: 14 }}>Esta obligación no tiene un cálculo automático.</div>
              )}

              {detalle.calc && detalle.calc.monto != null && (
                <>
                  <div className="divider" />
                  <Linea label="Monto estimado a pagar" value={fmt$(detalle.calc.monto)} bold color="#F59E0B" />
                </>
              )}

              {detalle.calc && detalle.calc.nota && (
                <div style={{ fontSize: 13, color: 'var(--silver)', marginTop: 12, lineHeight: 1.5 }}>
                  <Info size={13} style={{ verticalAlign: 'middle' }} /> {detalle.calc.nota}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 10 }}>
                Es una estimación informativa basada en lo registrado en el sistema. El monto oficial lo confirma tu contador.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────── NOTAS (recomendaciones/consultas)
const TIPO_NOTA = {
  consulta:      { label: 'Consulta', cls: 'badge-info' },
  recomendacion: { label: 'Recomendación', cls: 'badge-warning' },
  situacion:     { label: 'Situación', cls: 'badge-neutral' },
};
const PRIORIDAD = { alta: 'badge-danger', media: 'badge-warning', baja: 'badge-neutral' };

function Notas({ editable }) {
  const [filtro, setFiltro] = useState('abierta');
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [resolver, setResolver] = useState(null);

  const load = useCallback(async () => {
    setRows(await C.listNotas(filtro || undefined));
  }, [filtro]);
  useEffect(() => { load(); }, [load]);

  const nueva = () => setModal({ tipo: 'consulta', titulo: '', detalle: '', prioridad: 'media', fecha_limite: '' });

  const guardar = async () => {
    if (!modal.titulo.trim()) { alert('Escribe un título.'); return; }
    try { await C.saveNota({ ...modal, fecha_limite: modal.fecha_limite || null, estado: 'abierta' }); setModal(null); await load(); }
    catch (e) { alert('Error: ' + (e.message || e)); }
  };

  const confirmarResolver = async () => {
    try { await C.resolverNota(resolver.id, resolver.respuesta); setResolver(null); await load(); }
    catch (e) { alert('Error: ' + (e.message || e)); }
  };

  const eliminar = async n => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    try { await C.deleteNota(n.id); await load(); } catch (e) { alert('Error: ' + (e.message || e)); }
  };

  return (
    <div>
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['abierta', 'Abiertas'], ['resuelta', 'Resueltas'], ['', 'Todas']].map(([v, l]) => (
            <button key={v} className={filtro === v ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setFiltro(v)}>{l}</button>
          ))}
        </div>
        {editable && <button className="btn btn-primary btn-sm" onClick={nueva}><Plus size={15} /> Nueva nota</button>}
      </div>

      {rows.length === 0 ? (
        <div className="card empty-state">
          <MessageSquare size={40} className="empty-state-icon" />
          <div className="empty-state-title">Sin notas {filtro === 'abierta' ? 'abiertas' : ''}</div>
          <div>Aquí anotas situaciones a decidir, dudas para el contador y recomendaciones.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map(n => {
            const t = TIPO_NOTA[n.tipo] || TIPO_NOTA.consulta;
            return (
              <div key={n.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                      <span className={`badge ${t.cls}`}>{t.label}</span>
                      <span className={`badge ${PRIORIDAD[n.prioridad] || 'badge-neutral'}`}>Prioridad {n.prioridad}</span>
                      {n.estado === 'resuelta'
                        ? <span className="badge badge-success"><CheckCircle size={11} style={{ marginRight: 4 }} />Resuelta</span>
                        : <span className="badge badge-warning"><Clock size={11} style={{ marginRight: 4 }} />Abierta</span>}
                      <span style={{ fontSize: 12, color: 'var(--silver)' }}>{fmtFecha(n.creado_en)}</span>
                      {n.fecha_limite && <span className="badge badge-info"><CalendarClock size={11} style={{ marginRight: 4 }} />Vence {fmtFecha(n.fecha_limite)}</span>}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{n.titulo}</div>
                    {n.detalle && <div style={{ color: '#cdd9e5', fontSize: 14, lineHeight: 1.5 }}>{n.detalle}</div>}
                    {n.respuesta && (
                      <div style={{ marginTop: 10, padding: 10, background: 'rgba(34, 197, 94, 0.08)', borderRadius: 8, fontSize: 14, color: '#cdd9e5' }}>
                        <strong style={{ color: 'var(--text-success)' }}>Resolución:</strong> {n.respuesta}
                      </div>
                    )}
                  </div>
                  {editable && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {n.estado !== 'resuelta' && <button className="btn btn-success btn-sm" onClick={() => setResolver({ id: n.id, respuesta: '' })}>Resolver</button>}
                      <button className="btn btn-danger btn-icon" onClick={() => eliminar(n)}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Nueva nota</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={modal.tipo} onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))}>
                    <option value="consulta">Consulta (duda para el contador)</option>
                    <option value="recomendacion">Recomendación</option>
                    <option value="situacion">Situación a decidir</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={modal.prioridad} onChange={e => setModal(m => ({ ...m, prioridad: e.target.value }))}>
                    <option value="alta">Alta</option>
                    <option value="media">Media</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Título *</label>
                <input className="form-input" value={modal.titulo} placeholder="Ej: ¿La comisión de la póliza X lleva ITBMS?"
                  onChange={e => setModal(m => ({ ...m, titulo: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Detalle</label>
                <textarea className="form-textarea" value={modal.detalle} onChange={e => setModal(m => ({ ...m, detalle: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="form-label">Fecha de cumplimiento (opcional)</label>
                <input type="date" className="form-input" value={modal.fecha_limite || ''} onChange={e => setModal(m => ({ ...m, fecha_limite: e.target.value }))} />
                <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 4 }}>
                  Si pones fecha, aparecerá en el CRM del responsable 60 días antes.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {resolver && (
        <div className="modal-overlay" onClick={() => setResolver(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <div className="modal-title">Resolver nota</div>
              <button className="btn btn-secondary btn-icon" onClick={() => setResolver(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Resolución / decisión tomada</label>
                <textarea className="form-textarea" value={resolver.respuesta} onChange={e => setResolver(r => ({ ...r, respuesta: e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setResolver(null)}>Cancelar</button>
              <button className="btn btn-success" onClick={confirmarResolver}>Marcar resuelta</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────── CONFIGURACIÓN
function ConfigContable({ config, onSaved, editable }) {
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  useEffect(() => { setForm(config); }, [config]);
  useEffect(() => { C.listUsuarios().then(setUsuarios); }, []);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    setSaving(true); setMsg('');
    try {
      await C.updateConfig({
        tipo_contribuyente: form.tipo_contribuyente,
        inscrito_itbms: form.inscrito_itbms,
        comisiones_itbms: form.comisiones_itbms,
        cotiza_css: form.cotiza_css,
        tiene_empleados: form.tiene_empleados,
        responsable_usuario_id: form.responsable_usuario_id || null,
      });
      setMsg('Configuración guardada.');
      onSaved && onSaved();
    } catch (e) { setMsg('Error: ' + (e.message || e)); }
    finally { setSaving(false); }
  };

  const Switch = ({ k, label, hint }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <input type="checkbox" checked={!!form[k]} disabled={!editable} onChange={e => upd(k, e.target.checked)} style={{ marginTop: 4 }} />
      <div>
        <div style={{ fontWeight: 600 }}>{label}</div>
        {hint && <div style={{ fontSize: 13, color: 'var(--silver)', marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="card">
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Régimen y configuración fiscal</div>

        <div className="form-group" style={{ marginBottom: 16 }}>
          <label className="form-label">Tipo de contribuyente</label>
          <select className="form-select" value={form.tipo_contribuyente} disabled={!editable}
            onChange={e => upd('tipo_contribuyente', e.target.value)}>
            <option value="natural">Persona Natural</option>
            <option value="juridica">Persona Jurídica (S.A.)</option>
          </select>
          <div style={{ fontSize: 13, color: 'var(--silver)', marginTop: 6 }}>
            Cambia el cálculo del ISR (progresivo vs 25% fijo) y qué obligaciones aparecen en el calendario.
            Hoy estás como <strong>Persona Natural</strong>.
          </div>
        </div>

        <Switch k="inscrito_itbms" label="Estoy inscrito como contribuyente de ITBMS"
          hint="Solo si facturas más de $36,000/año en comisiones. Si lo activas, aparece la declaración mensual del Form. 430." />
        <Switch k="comisiones_itbms" label="Mis comisiones causan ITBMS (7%)"
          hint="Punto en gris en la ley — déjalo apagado hasta que tu contador/DGI lo confirme por escrito." />
        <Switch k="cotiza_css" label="Cotizo a la CSS como independiente"
          hint="Obligatorio desde la reforma 2025 (IVM 9.36%). Al activarlo aparece la cuota mensual de CSS." />
        <Switch k="tiene_empleados" label="Tengo empleados en planilla formal"
          hint="Activa planilla CSS patronal y el décimo tercer mes. Tu asistente actual es independiente (no aplica)." />

        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label">Responsable de tareas contables</label>
          <select className="form-select" value={form.responsable_usuario_id || ''} disabled={!editable}
            onChange={e => upd('responsable_usuario_id', e.target.value ? Number(e.target.value) : null)}>
            <option value="">Sin asignar</option>
            {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
          </select>
          <div style={{ fontSize: 13, color: 'var(--silver)', marginTop: 6 }}>
            A esta persona le aparecerán en el <strong>CRM de Gestiones</strong> las obligaciones y
            recomendaciones con fecha de cumplimiento, <strong>60 días antes</strong> de vencer. Debe tener
            acceso a Contabilidad y al CRM. Si queda "Sin asignar", no se crean recordatorios.
          </div>
        </div>

        {editable && (
          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
            <button className="btn btn-primary" onClick={guardar} disabled={saving}>{saving ? 'Guardando…' : 'Guardar configuración'}</button>
            {msg && <span style={{ color: msg.startsWith('Error') ? '#EF4444' : '#22C55E', fontSize: 14 }}>{msg}</span>}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 16, borderColor: 'rgba(var(--sky-rgb),0.3)' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} style={{ color: 'var(--sky)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 14, color: '#cdd9e5', lineHeight: 1.6 }}>
            Cuando Riesgo Unido pase a <strong>Persona Jurídica</strong>, solo cambia aquí el tipo de
            contribuyente y el sistema se reconfigura: ISR al 25%, aparecen Tasa Única ($300) e Impuesto
            de Aviso de Operación, y se ajusta el mínimo de la tasa de la Superintendencia.
          </div>
        </div>
      </div>
    </div>
  );
}
